import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { ErrorListPageResponse } from '@ansys/cloud-angular-client';
import {
  DIALOG_DATA,
  SnackBarService,
} from '@ansys-internal/awf-angular/shared';
import { ButtonComponent } from '@ansys-internal/awc-angular/buttons';
import { lastValueFrom } from 'rxjs';
import { JobsService } from '../../../../api';
import { DownloadFilesAction } from '../../../actions';
import { ActionContributionsService } from '@ansys-internal/awf-angular/contributions';
import { MimeType } from '../../../shared/enums/mime-type.enum';
import { HttpClient } from '@angular/common/http';
import { ResultsService } from '../../../shared/services/results.services';
import {
  AnalyticsEventPayload,
  SubmitAnalyticsEventAction,
} from '@ansys-internal/awf-angular/analytics';
import { AnalyticEventEnum } from '../../../shared/enums';
import { State } from '@ansys-internal/awf-angular/store';
import { JobState } from '../../../state/lib/jobs.state';
import { Icons, IconType} from "@ansys-internal/awc-angular/icon";

interface AdditionalData {
  designId: string;
  jobId: string;
  jobName: string;
  jobDockerTag: string;
  message?: string;
}
@Component({
    selector: 'app-job-error-dialog',
    imports: [ButtonComponent],
    templateUrl: './job-error-dialog.component.html',
    styleUrl: './job-error-dialog.component.scss'
})
export class JobErrorDialogComponent {
  protected message: string = '';
  protected errCode: string = '';
  protected downloadingLog: boolean = false;
  protected downloadingInput: boolean = false;
  private fallbackMessage: string = 'There was an error running this simulation.';
  protected readonly Icons = Icons;

  constructor(
    @Inject(DIALOG_DATA) public data: ErrorListPageResponse & AdditionalData,
    private jobService: JobsService,
    private actions: ActionContributionsService,
    public readonly http: HttpClient,
    private resultsService: ResultsService,
    private _cdr: ChangeDetectorRef,
    private snackbar: SnackBarService,
    private state: State
  ) {
    if (this.data?.errors?.[0]) {
      this.message = this.data.errors[0].simulations?.[0]?.statusComment ?? this.fallbackMessage;
      this.errCode = `Error Code: ${this.data.errors[0]?.errorCode ?? '0x00000000'}`;
    } else {
      this.message = this.data.message ?? '';
    }
  }

  protected async onDownloadLog(): Promise<void> {
    if (this.downloadingLog) return;
    this.downloadingLog = true;

    try {
      const job = this.state
        .value(JobState)
        .find((j) => j.jobId === this.data?.jobId);

      const simulationId = job?.simulations?.[0]?.simulationId ?? '';

      const requestBody = {
        job_id: this.data?.jobId as string,
        job_name: this.data?.jobName as string,
        docker_tag: this.data?.jobDockerTag as string,
        simulation_id: simulationId,
      };

      if (this.data?.errors?.[0]) {
        // There are errors — fetch the error log via the jobs error file endpoint
        const logContent = await lastValueFrom(
          this.jobService.requestConsoleLogJobsErrorFilePost(
            requestBody,
            this.data?.designId,
            null
          )
        );
        await this.actions.execute(DownloadFilesAction, [
          `${this.data?.jobName}-err.log`,
          logContent,
          MimeType.PLAIN_TEXT,
        ]);
      } else {
        // No errors — download the output results file with non-S3 / S3 fallback
        if (!job) {
          throw new Error('Job not found in state');
        }

        const blob = await this.resultsService.downloadOutputFile(job);
        const fileName = `${this.data?.jobName}-console.log`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.JOB_ERROR_LOG_DOWNLOAD,
        data: requestBody,
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    } catch (e) {
      this.snackbar.error('Failed to download log file');
    } finally {
      this.downloadingLog = false;
      this._cdr.detectChanges();
    }
  }

  protected async onDownloadInput(): Promise<void> {
    if (this.downloadingInput) return;
    this.downloadingInput = true;

    try {
      const blob = await lastValueFrom(
        this.resultsService.getJobFileAsBlob(
          this.data?.jobId as string,
          'cev_job.json'
        )
      );

      const fileName = `${this.data?.jobName ?? ''}-cev_job.json`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.JOB_INPUT_DOWNLOAD,
        data: { jobId: this.data?.request?.jobId, fileName },
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    } catch (error) {
      console.error('Failed to download input file:', error);
      this.snackbar.error('Failed to download input file');
    } finally {
      this.downloadingInput = false;
      this._cdr.detectChanges();
    }
  }
}
